<i18n>
{
  "cl": {
    "incomesText": "Agregar Renta",
    "debtText": "Agregar Deuda",
    "EligibilityText": "Elegibilidad",
    "equifaxPdfLink": "Descargar Equifax"
  },
  "pe": {
    "incomesText": "Agregar Renta",
    "debtText": "Agregar Deuda"
  },
  "br": {
    "incomesText": "Adicionar Salário",
    "debtText": "Adicionar Dívida"
  }
}
</i18n>
<template>
  <div class="debtors-list">
    <div v-for="debtor in debtors" :id="`debtor-${debtor.debtorIndex}`" :key="debtor.debtorIndex" class="debtor-item card-item debtors-card-shadow">
      <div class="debtor-header">
        <a
          v-if="debtor.type === 'CODEBTOR'"
          :id="`delete-codebtor-${debtor.debtorIndex}`"
          href="#"
          @click.prevent="$emit('delete-codebtor', debtor.debtorIndex)">
          <i class="fa-solid fa-trash-can" />
        </a>
        <div v-else />

        <div v-if="debtorIndexBeingChanged !== debtor.debtorIndex" class="debtor-header-id-number">
          <p>{{ debtor.debtorIndex === 0 ? 'Deudor' : `Codeudor ${debtor.idNumber || debtor.debtorIndex}` }}</p>
          <a
            v-if="debtor.type === 'CODEBTOR'"
            id="codebtor-pen-button"
            href="#"
            @click.prevent="changeCodebtorIdNumber(debtor.debtorIndex)">
            <i class="fa-solid fa-pen" />
          </a>
        </div>
        <b-field
          v-else
          id="codebtor-dni-input"
          :type="validIdNumber? 'is-success' : 'is-danger'"
          :message="messageIdNumber">
          <b-input
            v-model="idNumber"
            icon-clickable
            placeholder="RUT del codeudor"
            size="is-small"
            rounded />
        </b-field>

        <div class="debtor-header-buttons">
          <div v-if="debtor.type === 'CODEBTOR' && debtorIndexBeingChanged === debtor.debtorIndex" class="input-types">
            <button
              id="codebtor-dni-save-button"
              :class="extendedDebtor === debtor.debtorIndex && inputType==='incomes' ? 'is-primary' : ''"
              class="button is-button-secondary is-rounded"
              @click="saveChangedIdNumber(idNumber, debtor.debtorIndex)">
              <span>{{ 'Guardar' }}</span>
            </button>
            <button
              id="codebtor-dni-cancel-button"
              :class="extendedDebtor === debtor.debtorIndex && inputType==='debts' ? 'is-primary' : ''"
              class="button is-button-secondary is-rounded"
              @click="cancelChangedIdNumber()">
              <span>{{ 'Cancelar' }}</span>
            </button>
          </div>

          <div v-if="debtorIndexBeingChanged !== debtor.debtorIndex" class="input-types">
            <button
              id="income-menu-button"
              :class="extendedDebtor === debtor.debtorIndex && inputType==='incomes' ? 'is-primary' : ''"
              class="button is-button-secondary is-rounded"
              @click="$emit('set-extended-debtor', { debtorIndex: debtor.debtorIndex, inputType: 'incomes' })">
              <span>{{ $t('incomesText') }}</span>
            </button>
            <button
              id="debt-menu-button"
              :class="extendedDebtor === debtor.debtorIndex && inputType==='debts' ? 'is-primary' : ''"
              class="button is-button-secondary is-rounded"
              @click="$emit('set-extended-debtor', { debtorIndex: debtor.debtorIndex, inputType: 'debts' })">
              <span>{{ $t('debtText') }}</span>
            </button>
            <button
              v-if="featureToggleEligibilityButton"
              id="eligibility-menu-button"
              :class="extendedDebtor === debtor.debtorIndex && inputType==='eligibility' ? 'is-primary' : ''"
              class="button is-button-secondary is-rounded"
              @click="$emit('set-extended-debtor', { debtorIndex: debtor.debtorIndex, inputType: 'eligibility' })">
              <span>{{ $t('EligibilityText') }}</span>
            </button>
            <a
              v-if="debtor.type !== 'CODEBTOR' && equifaxPdfLink"
              id="equifaxPdfLink"
              :class="extendedDebtor === debtor.debtorIndex && inputType==='eligibility' ? 'is-primary' : ''"
              class="button is-button-secondary is-rounded"
              :href="equifaxPdfLink">
              <span>{{ $t('equifaxPdfLink') }}</span>
            </a>
          </div>
        </div>
      </div>
      <div class="debtor-inputs transition-fast" :class="extendedDebtor === debtor.debtorIndex ? 'show' : 'hide'">
        <div v-if="inputType==='incomes'">
          <IncomeInputs
            v-model="debtors[debtor.debtorIndex].income"
            :debtor-index="debtor.debtorIndex"
            v-bind="$attrs"
            @add-input="$emit('add-input', $event)"
            @delete-input="$emit('delete-input', $event)" />
        </div>
        <div v-else-if="inputType==='debts'">
          <DebtInputs
            v-model="debtors[debtor.debtorIndex].debt"
            :debtor-index="debtor.debtorIndex"
            v-bind="$attrs"
            @add-input="$emit('add-input', $event)"
            @delete-input="$emit('delete-input', $event)" />
        </div>
        <div v-else-if="inputType==='eligibility'">
          <EligibilityInputs
            v-model="debtors[debtor.debtorIndex].eligibility"
            :debtor-index="debtor.debtorIndex"
            @switch-eligibility-value="$emit('switch-eligibility-value', $event)" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import IncomeInputs from '@/ui/components/internal/risk-evaluation/IncomeInputs.vue';
import DebtInputs from '@/ui/components/internal/risk-evaluation/DebtInputs.vue';
import EligibilityInputs from '@/ui/components/internal/risk-evaluation/EligibilityInputs.vue';
import { IdNumberValidatorCL } from 'creditu-types/validators/id-number';
import { formattedToValue, valueToFormatted } from 'creditu-types/format';

export default {
  name: 'Debtors',
  components: {
    DebtInputs,
    IncomeInputs,
    EligibilityInputs
  },
  props: {
    value: { type: Array },
    extendedDebtor: { type: Number, default: null },
    inputType: { type: String, default: null },
    featureToggleEligibilityButton: { type: Boolean, default: false },
    equifaxPdfLink: { type: String, default: null }
  },
  data() {
    return {
      debtors: null,
      debtorIndexBeingChanged: null,
      idNumber: '',
      messageIdNumber: '',
      validIdNumber: ''
    };
  },
  watch: {
    idNumber: {
      handler(newValue) {
        const unformattedNewValue = formattedToValue('idNumber', this.$i18n.locale, newValue);
        const formattedNewValue = valueToFormatted('idNumber', this.$i18n.locale, unformattedNewValue);
        const idNumberValidatorCL = new IdNumberValidatorCL();
        const validIdNumber = idNumberValidatorCL.validate(unformattedNewValue);

        this.idNumber = formattedNewValue;
        this.unformattedIdNumber = unformattedNewValue;
        this.validIdNumber = validIdNumber;
        this.messageIdNumber = validIdNumber ? '' : 'Ingresa un RUT válido';
      }
    },
    /**
     * Si cambia debtors (v-model) en Evaluate.vue,
     * entonces actualizamos el objeto debtors.
     */
    value: {
      immediate: true,
      handler(vModelValues) {
        this.debtors = vModelValues;
      }
    },
    /**
     * Si cambia algún debtor, entonces se emite un evento input para que Evaluate.vue lo capture en la variable inputs.debtors asignada en v-model.
     */
    debtors: {
      immediate: true,
      deep: true,
      handler(val) {
        this.$emit('input', val);
      }
    }
  },
  methods: {
    changeCodebtorIdNumber(debtorIndex) {
      this.debtorIndexBeingChanged = debtorIndex;
      this.idNumber = '';
      this.messageIdNumber = '';
      this.validIdNumber = '';
    },
    cancelChangedIdNumber() {
      this.debtorIndexBeingChanged = null;
    },
    saveChangedIdNumber(idNumber, debtorIndex) {
      this.debtorIndexBeingChanged = null;
      this.$emit('save-changed-id-number', { debtorIndex, idNumber });
    }
  }
};
</script>
<style scoped>
.debtors-list {
  display: flex;
  flex-flow: column wrap;
  gap: 1rem;
}
.debtor-header {
  display: grid;
  grid-template-columns: 1rem 1fr 26rem;
  place-items: center stretch;
  gap: 0rem 1rem;
}
.debtor-header-id-number {
  display: grid;
  grid-template-columns: minmax(5rem, auto) 1fr;
  place-items: left start;
  place-content: stretch;
  gap: 0rem 1rem;
}

.debtor-header-content div {
  justify-self: end;
}
.debtor-inputs {
  /* border-top: 1px solid var(--color-g-inactive); */
  margin: 1rem 0rem;
}
.hide {
  max-height: 0;
  overflow: hidden;
  padding: 0;
  margin: 0;
}
.show {
  max-height: 1000rem;
  overflow: hidden;
}
.input-types {
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-end;
  gap: 0.5rem;
}
.inputs-title div {
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  gap: 0rem 1rem;
}
.inputs-title {
  display: grid;
  grid-template-columns: 1fr 1rem;
  align-items: center;
}
.debtors-card-shadow {
  border: .5px solid var(--color-g-inactive);
  box-shadow: none;
}
</style>
